import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, MapPin, Star, Shield, ChevronRight, Briefcase, ArrowRight, Filter, X, Plus } from "lucide-react";
import { http } from "../../lib/api";

function ListingCard({ listing }) {
  const rating = listing.avg_rating ? Number(listing.avg_rating).toFixed(1) : null;
  return (
    <Link to={`/marketplace/business/${listing.id}`} className="group block">
      <div className="bg-white rounded-2xl border border-gray-100 hover:shadow-md hover:border-yellow-200 transition-all overflow-hidden">
        {/* Cover */}
        <div className="relative h-36 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
          {listing.cover_image
            ? <img src={listing.cover_image} alt={listing.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            : <div className="w-full h-full grid place-items-center"><Briefcase size={32} className="text-gray-300" /></div>
          }
          {listing.featured && (
            <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold text-white bg-yellow-500">⭐ Featured</span>
          )}
          {listing.verified && (
            <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200"><Shield size={10} className="inline mr-0.5" />Verified</span>
          )}
        </div>
        {/* Info */}
        <div className="p-4">
          <div className="flex items-start gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-gray-100 shrink-0 border-2 border-white shadow-sm -mt-7">
              {listing.logo_url
                ? <img src={listing.logo_url} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full grid place-items-center text-base font-bold" style={{ background: "#0F1E38", color: "#C5A028" }}>{(listing.name || "?")[0]}</div>
              }
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <div className="font-bold text-gray-900 leading-tight truncate">{listing.name}</div>
              {listing.category_name && (
                <span className="text-xs text-yellow-700 font-semibold bg-yellow-50 px-2 py-0.5 rounded-full">{listing.category_name}</span>
              )}
            </div>
          </div>
          {listing.tagline && <p className="text-sm text-gray-600 line-clamp-2 leading-snug mb-2">{listing.tagline}</p>}
          <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap mt-2">
            {listing.location && <span className="flex items-center gap-1"><MapPin size={11} />{listing.location}</span>}
            {rating && <span className="flex items-center gap-1"><Star size={11} className="text-yellow-500 fill-yellow-500" />{rating} ({listing.review_count})</span>}
            {listing.response_time && <span>Responds {listing.response_time}</span>}
          </div>
          {listing.services?.length > 0 && (
            <div className="flex gap-1.5 flex-wrap mt-3">
              {listing.services.slice(0, 3).map((s, i) => (
                <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{s}</span>
              ))}
              {listing.services.length > 3 && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">+{listing.services.length - 3}</span>}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function MarketplaceHome() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const q = searchParams.get("q") || "";
  const category = searchParams.get("category") || "";
  const location = searchParams.get("location") || "";
  const sort = searchParams.get("sort") || "featured";
  const [localQ, setLocalQ] = useState(q);
  const [localLoc, setLocalLoc] = useState(location);

  const loadListings = useCallback(() => {
    setLoading(true);
    const params = { limit: 20, offset: (page - 1) * 20, sort };
    if (q) params.q = q;
    if (category) params.category = category;
    if (location) params.location = location;
    http.get("/marketplace/listings", { params })
      .then(r => { const d = r.data; setListings(d.items || d); setTotal(d.total || (d.items || d).length); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [q, category, location, sort, page]);

  useEffect(() => { loadListings(); }, [loadListings]);
  useEffect(() => {
    http.get("/marketplace/categories").then(r => setCategories(r.data)).catch(() => {});
    http.get("/marketplace/settings").then(r => setSettings(r.data)).catch(() => {});
    http.get("/marketplace/listings", { params: { featured: true, limit: 4 } }).then(r => setFeatured(r.data.items || r.data)).catch(() => {});
  }, []);

  const setParam = (key, val) => {
    const next = new URLSearchParams(searchParams);
    if (val) next.set(key, val); else next.delete(key);
    next.delete("page");
    setPage(1);
    setSearchParams(next);
  };

  const hero = settings.hero || {};
  const isFiltered = !!(q || category || location);

  return (
    <div className="space-y-12">
      {/* Hero */}
      {!isFiltered && (
        <section
          className="relative overflow-hidden rounded-3xl px-8 md:px-16 py-14 md:py-20 text-center"
          style={{ background: hero.bg_color || "linear-gradient(135deg, #0F1E38 0%, #1a3060 100%)" }}
        >
          {hero.image_url && <img src={hero.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />}
          <div className="relative z-10">
            {hero.label && <div className="inline-block text-xs font-bold uppercase tracking-[0.22em] px-3 py-1 rounded-full mb-5 bg-white/15 text-yellow-400">{hero.label}</div>}
            <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              {hero.heading || "Find Trusted Services in Your Community"}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8 max-w-xl mx-auto">
              {hero.subheading || "Connect with skilled members of the Celestial family — repairs, tutoring, catering, creative work, and more."}
            </p>
            <form onSubmit={e => { e.preventDefault(); setParam("q", localQ); setParam("location", localLoc); }} className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto">
              <div className="flex-1 flex items-center gap-2 bg-white rounded-2xl px-4 py-3.5 shadow-lg">
                <Search size={16} className="text-gray-400 shrink-0" />
                <input value={localQ} onChange={e => setLocalQ(e.target.value)} placeholder="What service do you need?" className="flex-1 text-sm outline-none text-gray-700" />
              </div>
              <div className="flex-1 sm:max-w-[200px] flex items-center gap-2 bg-white rounded-2xl px-4 py-3.5 shadow-lg">
                <MapPin size={16} className="text-gray-400 shrink-0" />
                <input value={localLoc} onChange={e => setLocalLoc(e.target.value)} placeholder="City or country" className="flex-1 text-sm outline-none text-gray-700" />
              </div>
              <button type="submit" className="px-8 py-3.5 rounded-2xl font-bold text-white shadow-lg whitespace-nowrap" style={{ background: "#C5A028" }}>Search</button>
            </form>
          </div>
        </section>
      )}

      {/* Categories */}
      {!isFiltered && categories.length > 0 && (
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: "Cormorant Garamond, serif" }}>Browse by Category</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {categories.map(c => (
              <Link key={c.id} to={`/marketplace?category=${encodeURIComponent(c.slug)}`}
                className="group flex flex-col items-center gap-2 p-4 rounded-2xl bg-white border border-gray-100 hover:border-yellow-300 hover:shadow-sm transition-all text-center"
              >
                <div className="text-2xl">{c.icon || "🔧"}</div>
                <div className="text-sm font-semibold text-gray-700 group-hover:text-yellow-600 leading-tight">{c.name}</div>
                {c.listing_count > 0 && <div className="text-xs text-gray-400">{c.listing_count} listings</div>}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Featured listings */}
      {!isFiltered && featured.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Cormorant Garamond, serif" }}>⭐ Featured Services</h2>
            <Link to="/marketplace?sort=featured" className="text-sm font-semibold text-yellow-600 flex items-center gap-1">View all <ArrowRight size={14} /></Link>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {featured.map(l => <ListingCard key={l.id} listing={l} />)}
          </div>
        </section>
      )}

      {/* All / filtered listings */}
      <section>
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {category ? categories.find(c => c.slug === category)?.name || "Services"
                : q ? `Results for "${q}"`
                : isFiltered ? "Matching Services"
                : "All Services"}
            </h2>
            {total > 0 && <div className="text-sm text-gray-500 mt-0.5">{total} listing{total !== 1 ? "s" : ""}</div>}
          </div>
          <div className="flex items-center gap-3">
            {isFiltered && (
              <button onClick={() => { setSearchParams(new URLSearchParams()); setLocalQ(""); setLocalLoc(""); }} className="flex items-center gap-1.5 text-sm font-semibold text-red-500 hover:text-red-600">
                <X size={14} /> Clear filters
              </button>
            )}
            <button onClick={() => setShowFilters(o => !o)} className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
              <Filter size={14} /> Filter
            </button>
            <select value={sort} onChange={e => setParam("sort", e.target.value)} className="px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white font-semibold text-gray-600">
              <option value="featured">Featured First</option>
              <option value="newest">Newest</option>
              <option value="rating">Highest Rated</option>
              <option value="reviews">Most Reviews</option>
            </select>
          </div>
        </div>

        {/* Inline filters */}
        {showFilters && (
          <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6 grid sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5 block">Search</label>
              <form onSubmit={e => { e.preventDefault(); setParam("q", localQ); }} className="flex gap-2">
                <input value={localQ} onChange={e => setLocalQ(e.target.value)} placeholder="Service or keyword" className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none" />
                <button type="submit" className="px-3 py-2 rounded-xl bg-gray-900 text-white text-xs font-bold">Go</button>
              </form>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5 block">Category</label>
              <select value={category} onChange={e => setParam("category", e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white">
                <option value="">All Categories</option>
                {categories.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5 block">Location</label>
              <input value={localLoc} onChange={e => { setLocalLoc(e.target.value); setParam("location", e.target.value); }} placeholder="City or country" className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none" />
            </div>
          </div>
        )}

        {/* Category chips */}
        {categories.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none mb-5">
            <button onClick={() => setParam("category", "")} className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${!category ? "text-white border-transparent" : "border-gray-200 text-gray-600 bg-white"}`} style={!category ? { background: "#0F1E38" } : {}}>All</button>
            {categories.map(c => (
              <button key={c.id} onClick={() => setParam("category", c.slug)} className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${category === c.slug ? "text-white border-transparent" : "border-gray-200 text-gray-600 bg-white"}`} style={category === c.slug ? { background: "#0F1E38" } : {}}>
                {c.icon && <span className="mr-1">{c.icon}</span>}{c.name}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 animate-pulse">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-64 rounded-2xl bg-gray-100" />)}
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20">
            <Briefcase size={48} className="mx-auto text-gray-200 mb-4" />
            <div className="text-lg font-semibold text-gray-500 mb-2">No listings found</div>
            <p className="text-sm text-gray-400 mb-6">Try a different category or location, or be the first to list in this area!</p>
            <Link to="/marketplace/create" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-white" style={{ background: "#0F1E38" }}>
              <Plus size={16} /> List Your Service
            </Link>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {listings.map(l => <ListingCard key={l.id} listing={l} />)}
            </div>
            {listings.length >= 20 && (
              <div className="text-center mt-8">
                <button onClick={() => setPage(p => p + 1)} className="px-8 py-3 rounded-full border border-gray-300 font-semibold text-sm text-gray-700 hover:bg-gray-50">Load More</button>
              </div>
            )}
          </>
        )}
      </section>

      {/* CTA for listing */}
      <section className="rounded-3xl p-8 md:p-12 text-center" style={{ background: "#0F1E38" }}>
        <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>Offer Your Skills to the Celestial Family</h2>
        <p className="text-white/70 text-sm leading-relaxed mb-6 max-w-md mx-auto">Self-employed? Running a small business? Let the community discover and trust your services.</p>
        <Link to="/marketplace/create" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-white" style={{ background: "#C5A028" }}>
          <Briefcase size={18} /> Create Your Listing <ArrowRight size={16} />
        </Link>
      </section>
    </div>
  );
}
